'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import BusinessInfoForm, { BusinessInfoData } from './BusinessInfoForm';
import ServiceSpecialization, { ServiceSpecializationData } from './ServiceSpecialization';
import DocumentUpload, { DocumentUploadData } from './DocumentUpload';
import ListingPreview from './ListingPreview';
import VerificationProcess from './VerificationProcess';

const ClaimListingInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);

  const [businessInfo, setBusinessInfo] = useState<BusinessInfoData>({
    shopName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'CA',
    zipCode: '',
    website: '',
    yearsInBusiness: ''
  });

  const [serviceData, setServiceData] = useState<ServiceSpecializationData>({
    brands: [],
    services: [],
    certifications: [],
    specialNotes: ''
  });

  const [documentData, setDocumentData] = useState<DocumentUploadData>({
    businessLicense: null,
    insurance: null,
    certifications: [],
    ownershipProof: null
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="h-96 bg-muted rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { number: 1, title: 'Business Info', icon: 'BuildingStorefrontIcon' },
    { number: 2, title: 'Services', icon: 'WrenchScrewdriverIcon' },
    { number: 3, title: 'Documents', icon: 'DocumentArrowUpIcon' },
    { number: 4, title: 'Review', icon: 'EyeIcon' }
  ];

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(
          businessInfo.shopName &&
          businessInfo.ownerName &&
          businessInfo.email &&
          businessInfo.phone &&
          businessInfo.address &&
          businessInfo.city &&
          businessInfo.zipCode &&
          businessInfo.yearsInBusiness
        );
      case 2:
        return serviceData.brands.length > 0 && serviceData.services.length > 0;
      case 3:
        return !!(
          documentData.businessLicense &&
          documentData.insurance &&
          documentData.ownershipProof
        );
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = () => {
    setShowSuccess(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl bg-card shadow-elevation-lg p-8 sm:p-12 text-center space-y-6">
            <div className="flex justify-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-success/10">
                <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="font-heading text-2xl font-semibold text-text-primary">
                Claim Submitted Successfully!
              </h2>
              <p className="font-body text-base text-text-secondary">
                Thank you for claiming your listing on AutoFixIQ
              </p>
            </div>
            <div className="rounded-lg bg-muted p-6 text-left space-y-4">
              <div className="flex items-start space-x-3">
                <Icon name="ClockIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-body text-sm font-medium text-text-primary mb-1">
                    What happens next?
                  </p>
                  <ul className="font-body text-sm text-text-secondary space-y-2">
                    <li>• Our verification team will review your documents within 1-2 business days</li>
                    <li>• You'll receive a phone call to confirm your business details</li>
                    <li>• Once verified, your enhanced listing will go live immediately</li>
                    <li>• You'll receive login credentials to manage your listing</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="rounded-lg bg-primary/5 p-6 text-left">
              <div className="flex items-start space-x-3">
                <Icon name="EnvelopeIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-body text-sm font-medium text-text-primary mb-1">
                    Confirmation Email Sent
                  </p>
                  <p className="font-body text-sm text-text-secondary">
                    We've sent a confirmation email to <span className="font-medium text-primary">{businessInfo.email}</span> with your application details and next steps.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                onClick={() => window.location.href = '/home-page'}
                className="flex items-center justify-center space-x-2 rounded-lg bg-primary px-6 py-3 font-body text-sm font-medium text-primary-foreground transition-smooth hover:opacity-90 active:scale-[0.97] flex-1"
              >
                <Icon name="HomeIcon" size={18} />
                <span>Back to Home</span>
              </button>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  setCurrentStep(1);
                  setBusinessInfo({
                    shopName: '',
                    ownerName: '',
                    email: '',
                    phone: '',
                    address: '',
                    city: '',
                    state: 'CA',
                    zipCode: '',
                    website: '',
                    yearsInBusiness: ''
                  });
                  setServiceData({
                    brands: [],
                    services: [],
                    certifications: [],
                    specialNotes: ''
                  });
                  setDocumentData({
                    businessLicense: null,
                    insurance: null,
                    certifications: [],
                    ownershipProof: null
                  });
                }}
                className="flex items-center justify-center space-x-2 rounded-lg border border-border bg-card px-6 py-3 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:text-primary active:scale-[0.97] flex-1"
              >
                <Icon name="PlusIcon" size={18} />
                <span>Submit Another</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center lg:text-left">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary mb-3">
            Claim Your Business Listing
          </h1>
          <p className="font-body text-base text-text-secondary max-w-2xl mx-auto lg:mx-0">
            Verify your ownership and unlock enhanced features to connect with more customers
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <button
                    onClick={() => {
                      if (step.number < currentStep || validateStep(step.number - 1)) {
                        setCurrentStep(step.number);
                      }
                    }}
                    disabled={step.number > currentStep && !validateStep(step.number - 1)}
                    className={`flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full transition-smooth ${
                      currentStep === step.number
                        ? 'bg-primary text-primary-foreground'
                        : currentStep > step.number
                        ? 'bg-success text-success-foreground'
                        : 'bg-muted text-muted-foreground'
                    } ${
                      step.number < currentStep || validateStep(step.number - 1)
                        ? 'cursor-pointer hover:opacity-80' :'cursor-not-allowed'
                    }`}
                  >
                    {currentStep > step.number ? (
                      <Icon name="CheckIcon" size={20} />
                    ) : (
                      <Icon name={step.icon as any} size={20} />
                    )}
                  </button>
                  <span className={`mt-2 font-caption text-xs font-medium hidden sm:block ${
                    currentStep === step.number
                      ? 'text-primary'
                      : currentStep > step.number
                      ? 'text-success' :'text-muted-foreground'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-2 ${
                    currentStep > step.number ? 'bg-success' : 'bg-border'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="rounded-xl bg-card shadow-elevation-md p-6 sm:p-8">
              {currentStep === 1 && (
                <BusinessInfoForm
                  onDataChange={setBusinessInfo}
                  initialData={businessInfo}
                />
              )}
              {currentStep === 2 && (
                <ServiceSpecialization
                  onDataChange={setServiceData}
                  initialData={serviceData}
                />
              )}
              {currentStep === 3 && (
                <DocumentUpload
                  onDataChange={setDocumentData}
                  initialData={documentData}
                />
              )}
              {currentStep === 4 && (
                <ListingPreview
                  shopName={businessInfo.shopName}
                  address={businessInfo.address}
                  city={businessInfo.city}
                  state={businessInfo.state}
                  brands={serviceData.brands}
                  services={serviceData.services}
                />
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                <button
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className="flex items-center space-x-2 rounded-lg border border-border bg-card px-6 py-3 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:text-primary active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Icon name="ChevronLeftIcon" size={18} />
                  <span>Previous</span>
                </button>

                {currentStep < 4 ? (
                  <button
                    onClick={handleNext}
                    disabled={!validateStep(currentStep)}
                    className="flex items-center space-x-2 rounded-lg bg-primary px-6 py-3 font-body text-sm font-medium text-primary-foreground transition-smooth hover:opacity-90 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>Next Step</span>
                    <Icon name="ChevronRightIcon" size={18} />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    className="flex items-center space-x-2 rounded-lg bg-success px-6 py-3 font-body text-sm font-medium text-success-foreground transition-smooth hover:opacity-90 active:scale-[0.97]"
                  >
                    <Icon name="CheckIcon" size={18} />
                    <span>Submit Claim</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <VerificationProcess />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimListingInteractive;